import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Boxes, Package, Trash2, FilePlus2 } from 'lucide-react';
import axios from 'axios';

const MaterialsInventoryList = () => {
    const [logs, setLogs] = useState([]);
    const [error, setError] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        try {
            setIsLoading(true);
            const res = await axios.get('/api/material-inventory-logs');
            setLogs(res.data);
        } catch (err) {
            console.error('Error fetching inventory logs:', err);
            setError('Failed to load inventory logs');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateLog = async () => {
        setIsCreating(true);
        try {
            const res = await axios.post('/api/material-inventory-logs', {
                inventoryDate: new Date().toISOString().split('T')[0]
            });

            if (res.data && res.data.logID) {
                navigate(`/material-inventory/${res.data.logID}`);
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (err) {
            console.error('Error creating inventory log:', err.response?.data || err.message);
            setError(err.response?.data?.message || 'Failed to create new inventory log');
        } finally {
            setIsCreating(false);
        }
    };

    const handleDelete = async (e, logID) => {
        e.stopPropagation(); // Prevent row click when deleting
        
        if (!window.confirm('Are you sure you want to delete this inventory log?')) {
            return;
        }

        try {
            await axios.delete(`/api/material-inventory-logs/${logID}`);
            setLogs(logs.filter(log => log.logID !== logID));
        } catch (err) {
            console.error('Error deleting inventory log:', err);
            setError('Failed to delete inventory log');
        }
    };

    const handleRowClick = (logID) => {
        navigate(`/material-inventory/${logID}`);
    };

    if (isLoading) {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="sticky-top bg-light border-bottom" style={{ top: '0', zIndex: 1020 }}>
                <div className="container-fluid py-2">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <Boxes size={18} className="me-2" />
                            <h5 className="mb-0">Material Inventories</h5>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <button
                                className="btn btn-outline-secondary d-flex align-items-center"
                                onClick={() => navigate('/inventory')}
                            >
                                <Package size={18} className="me-2" />
                                Bottled Inventory
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreateLog}
                                disabled={isCreating}
                            >
                                <FilePlus2 size={18} className="me-2" />
                                {isCreating ? 'Creating...' : 'New Inventory Count'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mt-4">
                {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {error}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setError(null)}
                        ></button>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Items</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => (
                                        <tr 
                                            key={log.logID}
                                            onClick={() => handleRowClick(log.logID)}
                                            style={{ cursor: 'pointer' }}
                                            className="table-row-hover"
                                        >
                                            <td>{new Date(log.inventoryDate).toLocaleDateString()}</td>
                                            <td>{log.itemCount} items</td>
                                            <td>
                                                <span className="badge bg-success">Completed</span>
                                            </td>
                                            <td className="text-end">
                                                <div className="btn-group">
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/material-inventory/${log.logID}`);
                                                        }}
                                                        title="View Details"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={(e) => handleDelete(e, log.logID)}
                                                        title="Delete Log"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <style>
                {`
                    .table-row-hover:hover td {
                        background-color: rgba(0,0,0,.075);
                    }
                    .table-row-hover {
                        transition: background-color 0.2s ease;
                    }
                `}
            </style>
        </>
    );
};

export default MaterialsInventoryList;